import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { getChangesetErrorsForProperty } from 'donor-elf-web/lib/changeset-utils';
import dateUtils from 'donor-elf-web/lib/date-utils';
import {
  type AppliedPropertyFilter,
  AvailableOperationsByPropertyType,
  AvailableOperationsForSingleSelectionList,
  GetOperationLabel,
  type Operation,
  OperationsWithoutVal,
  type PropertyFilterInfo,
} from 'donor-elf-web/lib/filter-utils-new';
import type { SelectOption } from 'donor-elf-web/lib/form-component-utils';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import validateFilter from 'donor-elf-web/validations/applied-property-filter';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import {
  type FormListSelectionChangedArgs,
  FormListSelectionOption,
} from '../form/list-selection';
import type { FormUnderlinedSelectContactOnChangeOptions } from '../form/underline/select-contact';

interface ComponentArgs {
  isNew: boolean; // true if it's a new filter being added
  appliedPropertyFilter?: AppliedPropertyFilter; // will be undefined if it's a new filter
  availablePropertyFilters: PropertyFilterInfo[];
  onAdd: (appliedPropertyFilter: AppliedPropertyFilter) => void; // fired when a new property filter is applied
  onChange: (appliedPropertyFilter: AppliedPropertyFilter) => void; // fired when an existing property filter is edited and applied
  onCancel: () => void;
}

export default class FilterEditComponent extends Component<ComponentArgs> {
  constructor(owner: any, args: ComponentArgs) {
    super(owner, args);
    this.createChangeset();
    this.populateValOptionsForListSelectionIfNecessary();
    this.populateContactListOptionsIfNecessary();
  }

  @service declare contactsCache: ContactsCache;

  @tracked changeset: any;
  @tracked showValidationErrors = false;
  @tracked valOptionsForListSelection?: FormListSelectionOption[];
  selectedValOptions?: FormListSelectionOption[];

  @tracked contactListOptions?: FormListSelectionOption[];
  selectedContactListOptions?: FormListSelectionOption[];

  get selectedPropertyName() {
    return this.changeset?.get('propertyName');
  }

  get selectedOperation(): Operation | undefined {
    return this.changeset?.get('operation');
  }

  get selectedPropertyFilterInfo() {
    return this.args.availablePropertyFilters.find(
      (x) => x.propertyName === this.selectedPropertyName,
    );
  }

  get selectedPropertyType() {
    return this.selectedPropertyFilterInfo?.propertyType;
  }

  get propertyNameOptions(): SelectOption[] {
    return this.args.availablePropertyFilters
      .filter((x) => !x.systemOnly)
      .map((x) => {
        return {
          val: x.propertyName,
          label: x.label,
        };
      });
  }

  get showOperations() {
    return (
      isPresent(this.selectedPropertyName) &&
      this.selectedPropertyType !== 'contactIds' &&
      this.selectedPropertyType !== 'no-operation' &&
      !this.selectedPropertyIsStringWithListOptions &&
      !this.selectedPropertyTypeIsBooleanWithOneOperation
    );
  }

  get operationsSelectDropdownInitiallyOpened() {
    return (
      this.args.isNew &&
      this.operationSelectOptions.length > 1 &&
      this.selectedPropertyType !== 'list'
    );
  }

  get valSelectIsInitiallyOpenedForStringWithListOptions() {
    return this.args.isNew && this.selectedPropertyIsStringWithListOptions;
  }

  get selectedPropertyIsStringWithListOptions() {
    return (
      this.selectedPropertyType === 'string' &&
      (this.selectedPropertyFilterInfo?.listOptions?.length || 0) > 0
    );
  }

  get selectedPropertyTypeIsBooleanWithOneOperation() {
    return (
      this.selectedPropertyType === 'boolean' &&
      this.operationSelectOptions.length === 1
    );
  }

  @cached
  get operationSelectOptions(): SelectOption[] {
    let { selectedPropertyType, selectedPropertyFilterInfo } = this;
    if (
      selectedPropertyType != null &&
      selectedPropertyType !== 'no-operation'
    ) {
      let availableOperations =
        selectedPropertyFilterInfo?.availableOperations ||
        AvailableOperationsByPropertyType[selectedPropertyType];
      if (
        selectedPropertyType === 'list' &&
        selectedPropertyFilterInfo?.listIsSingleSelection
      ) {
        availableOperations = AvailableOperationsForSingleSelectionList;
        if (
          selectedPropertyFilterInfo?.availableOperations &&
          selectedPropertyFilterInfo.availableOperations.some(
            (x) => !AvailableOperationsForSingleSelectionList.includes(x),
          )
        ) {
          alert(
            `${selectedPropertyFilterInfo!.label} is a single selection list and the only allowed operations are ${AvailableOperationsForSingleSelectionList.join(', ')}`,
          );
        }
      }
      if (this.selectedPropertyIsStringWithListOptions) {
        return [
          {
            val: '=',
            label: GetOperationLabel('='),
          },
        ];
      }
      return availableOperations.map((x) => {
        return {
          val: x,
          label: GetOperationLabel(x, { propertyType: selectedPropertyType }),
        };
      });
    } else {
      return [];
    }
  }

  get showVal() {
    return (
      isPresent(this.selectedPropertyName) &&
      isPresent(this.selectedOperation) &&
      !OperationsWithoutVal[this.selectedOperation!]
    );
  }

  populateValOptionsForListSelectionIfNecessary() {
    let { selectedPropertyFilterInfo } = this;
    if (selectedPropertyFilterInfo?.listOptions) {
      let currentValOptions = this.changeset.get('val') || [];
      if (typeof currentValOptions === 'string') {
        currentValOptions = [currentValOptions];
      }
      let items: FormListSelectionOption[] = [];
      for (let [
        index,
        val,
      ] of selectedPropertyFilterInfo.listOptions.entries()) {
        if (typeof val === 'string') {
          items.push(
            new FormListSelectionOption({
              val,
              selected: currentValOptions.includes(val),
              index,
            }),
          );
        } else {
          let selectOption = val as SelectOption;
          items.push(
            new FormListSelectionOption({
              val: selectOption.val,
              label: selectOption.label,
              selected: currentValOptions.includes(selectOption.val),
              index,
            }),
          );
        }
      }
      this.valOptionsForListSelection = sortByProperties(items, [
        'selected:desc',
        'index:asc',
      ]);
      this.selectedValOptions = this.valOptionsForListSelection.filter(
        (x) => x.selected,
      );
    } else if (this.valOptionsForListSelection) {
      this.valOptionsForListSelection = undefined;
      this.selectedValOptions = undefined;
    }
  }

  populateContactListOptionsIfNecessary() {
    if (this.selectedPropertyFilterInfo?.propertyType === 'contactIds') {
      let selectedContactIds = new Set(this.changeset.get('val') || []);
      let items: FormListSelectionOption[] = [];
      for (let [
        index,
        contact,
      ] of this.contactsCache.sortedContacts.entries()) {
        items.push(
          new FormListSelectionOption({
            val: contact.id,
            label: contact.name,
            selected: selectedContactIds.has(contact.id),
            index,
            valIsContactId: true,
          }),
        );
      }
      this.contactListOptions = sortByProperties(items, [
        'selected:desc',
        'index:asc',
      ]);
      this.selectedContactListOptions = this.contactListOptions.filter(
        (x) => x.selected,
      );
    } else if (this.contactListOptions) {
      this.contactListOptions = undefined;
      this.selectedContactListOptions = undefined;
    }
  }

  get valOptionsForSelect(): SelectOption[] {
    return (this.valOptionsForListSelection || []).map((x) => {
      return {
        val: x.val,
        label: x.displayLabel,
      };
    });
  }

  @action
  handleListSelectionsChanged(args: FormListSelectionChangedArgs) {
    this.selectedValOptions = args.selectedListOptions;
  }

  @action
  handleContactListSelectionsChanged(args: FormListSelectionChangedArgs) {
    this.selectedContactListOptions = args.selectedListOptions;
  }

  get applyButtonDisabled() {
    return (
      isEmpty(this.selectedPropertyName) ||
      (this.selectedPropertyType !== 'no-operation' &&
        isEmpty(this.selectedOperation))
    );
  }

  @action
  createChangeset() {
    let appliedPropertyFilter = this.args.appliedPropertyFilter ?? {};
    let validation = validateFilter(this.args.availablePropertyFilters);
    this.changeset = Changeset(
      appliedPropertyFilter,
      lookupValidator(validation),
      validation,
    );
  }

  @action
  handlePropertyNameChanged(_val: string | undefined) {
    this.changeset.set('val', undefined);
    this.changeset.set('operation', undefined);
    if (this.operationSelectOptions?.length === 1) {
      this.changeset.set('operation', this.operationSelectOptions![0].val);
    }
    if (this.showOperations || this.selectedPropertyIsStringWithListOptions) {
      this.populateValOptionsForListSelectionIfNecessary();
      if (
        this.selectedPropertyType === 'list' &&
        this.selectedOperation == null
      ) {
        // auto select the first operation when selecting a list property
        this.changeset.set('operation', this.operationSelectOptions?.[0]?.val);
      }
    }
    this.populateContactListOptionsIfNecessary();
  }

  @action
  handleOperationChanged(operation: Operation | undefined) {
    if (operation == null || OperationsWithoutVal[operation]) {
      this.changeset.set('val', undefined);
    }
    this.populateValOptionsForListSelectionIfNecessary();
    this.populateContactListOptionsIfNecessary();
  }

  get validationErrorForListVal(): string | undefined {
    if (this.showValidationErrors) {
      return getChangesetErrorsForProperty(this.changeset, 'val')?.join(', ');
    } else {
      return undefined;
    }
  }

  get validationErrorForContactIdVal(): string | undefined {
    if (this.showValidationErrors) {
      return getChangesetErrorsForProperty(this.changeset, 'val')?.join(', ');
    } else {
      return undefined;
    }
  }

  @action
  handleContactChanged(
    _contactId: string,
    onChangeOptions?: FormUnderlinedSelectContactOnChangeOptions,
  ) {
    if (onChangeOptions?.selectedByKeyboardEnterEvent) {
      this.handleApplyClicked();
    }
  }

  @action
  async handleApplyClicked() {
    if (this.applyButtonDisabled) {
      return;
    }
    if (
      this.selectedPropertyType === 'list' &&
      this.selectedOperation !== '='
    ) {
      this.changeset.set(
        'val',
        this.selectedValOptions!.map((x) => x.val),
      );
    }
    if (this.selectedPropertyType === 'contactIds') {
      this.changeset.set(
        'val',
        this.selectedContactListOptions!.map((x) => x.val),
      );
    }
    if (
      this.selectedPropertyType === 'date' &&
      this.selectedOperation !== '><'
    ) {
      let currentVal = this.changeset.get('val');
      if (
        currentVal &&
        typeof currentVal === 'object' &&
        typeof currentVal.getDate === 'function'
      ) {
        currentVal = dateUtils.serializeToDateOnly(currentVal);
        this.changeset.set('val', currentVal);
      }
    }
    await this.changeset.validate();
    if (this.changeset.get('isValid')) {
      this.showValidationErrors = false;
      this.changeset.execute();
      if (this.selectedPropertyType === 'giftHistory') {
        let changesetData = this.changeset.data as any;
        changesetData.val = {
          val: changesetData.giftHistoryVal,
          valType: changesetData.giftHistoryValType,
          from: changesetData.giftHistoryFrom,
          to: changesetData.giftHistoryTo,
        };
        delete changesetData.giftHistoryVal;
        delete changesetData.giftHistoryValType;
        delete changesetData.giftHistoryFrom;
        delete changesetData.giftHistoryTo;
      } else if (this.selectedPropertyType === 'distanceFrom') {
        const changesetData = this.changeset.data as any;

        changesetData.val = {
          distance: changesetData.distanceFromDistance,
          units: changesetData.distanceFromUnits ?? 'miles',
          location: changesetData.distanceFromLocation,
        };

        delete changesetData.distanceFromDistance;
        delete changesetData.distanceFromUnits;
        delete changesetData.distanceFromLocation;
      } else if (
        this.selectedPropertyType === 'date' &&
        this.selectedOperation === '><'
      ) {
        let changesetData = this.changeset.data as any;
        changesetData.val = [
          changesetData.dateBetweenAfterVal,
          changesetData.dateBetweenBeforeVal,
        ];
        delete changesetData.dateBetweenAfterVal;
        delete changesetData.dateBetweenBeforeVal;
      }
      let appliedFilter: AppliedPropertyFilter = this.changeset
        .data as AppliedPropertyFilter;
      if (this.args.isNew) {
        this.args.onAdd(appliedFilter);
      } else {
        this.args.onChange(appliedFilter);
      }
    } else {
      this.showValidationErrors = true;
    }
  }
}
